import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment.development';

export interface TipoCrypto {
  tipo: string;
  nombre: string;
  redes: string[];
  ejemplo_address: string;
}

export interface TiposCryptoResponse {
  ok: boolean;
  tipos_soportados: TipoCrypto[];
}

export interface WalletInfo {
  wallet_crypto_address: string;
  wallet_tipo: string;
  wallet_red: string;
  wallet_verificada: boolean;
  wallet_fecha_registro: string;
  metodo_pago_preferido: 'crypto' | 'transferencia' | 'paypal';
  wallet_configurada: boolean;
}

export interface WalletResponse {
  ok: boolean;
  wallet: WalletInfo;
  comisiones?: {
    pendientes_cobro: number;
    ya_cobradas: number;
  };
}

export interface ConfigurarWalletRequest {
  wallet_crypto_address: string;
  wallet_tipo: string;
  wallet_red: string;
  metodo_pago_preferido: 'crypto' | 'transferencia' | 'paypal';
}

export interface ConfigurarWalletResponse {
  ok: boolean;
  msg: string;
  wallet: WalletInfo;
}

@Injectable({
  providedIn: 'root'
})
export class WalletService {
  private apiUrl = `${environment.apiUrl}/wallet`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene los tipos de crypto soportados
   * GET /api/wallet/tipos-crypto
   */
  obtenerTiposCrypto(): Observable<TiposCryptoResponse> {
    return this.http.get<TiposCryptoResponse>(`${this.apiUrl}/tipos-crypto`).pipe(
      tap(response => console.log('💎 Tipos de crypto soportados:', response)),
      catchError(error => {
        console.error('❌ Error al obtener tipos de crypto:', error);
        return of({ ok: false, tipos_soportados: [] });
      })
    );
  }

  /**
   * Obtiene la wallet configurada del usuario actual
   * GET /api/wallet
   */
  obtenerMiWallet(): Observable<WalletResponse> {
    return this.http.get<WalletResponse>(`${this.apiUrl}`).pipe(
      tap(response => console.log('💳 Mi wallet:', response)),
      catchError(error => {
        console.error('❌ Error al obtener wallet:', error);
        return of({ 
          ok: false, 
          wallet: {
            wallet_crypto_address: '',
            wallet_tipo: '',
            wallet_red: '',
            wallet_verificada: false,
            wallet_fecha_registro: '',
            metodo_pago_preferido: 'crypto' as const,
            wallet_configurada: false
          }
        } as WalletResponse);
      })
    );
  }

  /**
   * Configura una nueva wallet
   * POST /api/wallet
   */
  configurarWallet(request: ConfigurarWalletRequest): Observable<ConfigurarWalletResponse> {
    console.log('📤 Configurando wallet:', request);
    return this.http.post<ConfigurarWalletResponse>(`${this.apiUrl}`, request).pipe(
      tap(response => console.log('✅ Wallet configurada:', response)),
      catchError(error => {
        console.error('❌ Error al configurar wallet:', error);
        return of({ 
          ok: false, 
          msg: error.error?.msg || 'Error al configurar wallet',
          wallet: request as any
        });
      })
    );
  }

  /**
   * Actualiza la wallet existente
   * PUT /api/wallet
   */
  actualizarWallet(request: ConfigurarWalletRequest): Observable<ConfigurarWalletResponse> {
    console.log('📤 Actualizando wallet:', request);
    return this.http.put<ConfigurarWalletResponse>(`${this.apiUrl}`, request).pipe(
      tap(response => console.log('✅ Wallet actualizada:', response)),
      catchError(error => {
        console.error('❌ Error al actualizar wallet:', error);
        return of({ 
          ok: false, 
          msg: error.error?.msg || 'Error al actualizar wallet',
          wallet: request as any
        });
      })
    );
  }

  /**
   * Elimina la wallet configurada
   * DELETE /api/wallet
   */
  eliminarWallet(): Observable<{ ok: boolean; msg: string }> {
    return this.http.delete<{ ok: boolean; msg: string }>(`${this.apiUrl}`).pipe(
      tap(response => console.log('✅ Wallet eliminada:', response)),
      catchError(error => {
        console.error('❌ Error al eliminar wallet:', error);
        return of({ ok: false, msg: error.error?.msg || 'Error al eliminar wallet' });
      })
    );
  }

  /**
   * Verifica la wallet
   * POST /api/wallet/verificar
   */
  verificarWallet(): Observable<{ ok: boolean; msg: string }> {
    return this.http.post<{ ok: boolean; msg: string }>(`${this.apiUrl}/verificar`, {}).pipe(
      tap(response => console.log('✅ Wallet verificada:', response)),
      catchError(error => {
        console.error('❌ Error al verificar wallet:', error);
        return of({ ok: false, msg: error.error?.msg || 'Error al verificar wallet' });
      })
    );
  }

  /**
   * Valida el formato básico de una dirección de wallet
   * @param address - Dirección de la wallet
   * @param tipo - Tipo de crypto (BTC, ETH, USDT, etc.)
   * @param red - Red (TRC20, ERC20, BSC, Bitcoin, Ethereum, etc.)
   */
  validarFormatoWallet(address: string, tipo: string, red?: string): boolean {
    if (!address || address.length < 20) {
      return false;
    }

    // Para USDT, USDC y otros tokens, la validación depende de la red
    if ((tipo === 'USDT' || tipo === 'USDC') && red) {
      if (red === 'TRC20') {
        // Tron: empieza con T + 33 caracteres alfanuméricos
        return /^T[a-zA-Z0-9]{33}$/.test(address);
      } else if (red === 'ERC20' || red === 'BSC' || red === 'Polygon') {
        // Ethereum y compatibles: 0x + 40 caracteres hex
        return /^0x[a-fA-F0-9]{40}$/.test(address);
      }
    }

    switch (tipo) {
      case 'BTC':
        // Bitcoin: empieza con 1, 3 o bc1
        return /^(1|3|bc1)[a-zA-Z0-9]{25,}$/.test(address);
      
      case 'ETH':
      case 'BNB':
        // Ethereum y tokens ERC20/BSC: 0x + 40 caracteres hex
        return /^0x[a-fA-F0-9]{40}$/.test(address);
      
      case 'TRX':
        // Tron: empieza con T + 33 caracteres alfanuméricos
        return /^T[a-zA-Z0-9]{33}$/.test(address);
      
      default:
        // Validación genérica: al menos 20 caracteres
        return address.length >= 20;
    }
  }
}

